import React, { useState, useMemo, useEffect } from 'react'
import { Card, DataTable, Button, Input, PageHeader, CourierDetailModal, RejectModal, ApproveModal, ReopenModal, DatePicker, SectionTabs } from '@/shared/ui'
import { usePendingCouriers, useApproveCourier, useRejectCourier, useReopenCourier, usePagination, useDebounce, useUserDetails, useModerationHistory, useRegions, useTransportTypesList } from '@/shared/hooks'
import { useAuth } from '@/shared/contexts'
import { UserPlusIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import type { ModerationItemResponse, Column } from '@/shared/ui'
import type { ModerationListRequest } from '@/shared/api'

// Границы суток считаем в московском времени (UTC+3), независимо от таймзоны
// браузера, так как список отображает created_at в МСК.
// 00:00 МСК = 21:00 UTC предыдущего дня.
// created_from — начало мсковских суток, created_to — начало следующих суток
// (исключительно), чтобы диапазон включал весь выбранный день.
const dayStartMsk = (d: Date): number =>
  Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 1000) - 3 * 3600
const dayEndMskExclusive = (d: Date): number => dayStartMsk(d) + 86400

export const ModerationPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const { page, limit, setPage } = usePagination({ initialLimit: 50 })
  
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)
  
  // Фильтры-справочники
  const [regionFilter, setRegionFilter] = useState<number | ''>('')
  const [transportFilter, setTransportFilter] = useState<number | ''>('')

  // Даты: одиночная дата и период взаимно исключают друг друга
  const [singleDate, setSingleDate] = useState<Date | null>(null)
  const [periodFrom, setPeriodFrom] = useState<Date | null>(null)
  const [periodTo, setPeriodTo] = useState<Date | null>(null)

  // Фильтр по типу модерации
  const [moderationType, setModerationType] = useState<string>('')

  // Фильтр по статусу заявки: '' = модерация+отклонённые (значение бэкенда по умолчанию)
  const [statusFilter, setStatusFilter] = useState<string>('')

  // Состояние модального окна пересмотра заявки
  const [reopenTarget, setReopenTarget] = useState<{ userId: number; fullName: string } | null>(null)

  // Справочники для фильтров
  const { data: regions } = useRegions()
  const { data: transportTypes } = useTransportTypesList({ page: 1, limit: 100 })

  // Состояние модального окна
  const [selectedCourierId, setSelectedCourierId] = useState<number | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)

  // Состояние модального окна отклонения
  const [rejectTarget, setRejectTarget] = useState<{ userId: number; fullName: string } | null>(null)

  // Состояние модального окна одобрения (с выбором типа курьера)
  const [approveTarget, setApproveTarget] = useState<
    { userId: number; fullName: string; currentRole: number | undefined } | null
  >(null)

  const approveCourier = useApproveCourier()
  const rejectCourier = useRejectCourier()
  const reopenCourier = useReopenCourier()

  // Параметры запроса
  const queryParams = useMemo(() => ({
    page,
    limit,
    search: debouncedSearch || undefined,
    moderation_type: moderationType || undefined,
    status: (statusFilter || undefined) as ModerationListRequest['status'],
    region_id: regionFilter ? Number(regionFilter) : undefined,
    transport_type_id: transportFilter ? Number(transportFilter) : undefined,
    // Одиночная дата имеет приоритет над периодом (взаимно исключающие фильтры)
    created_from: singleDate ? dayStartMsk(singleDate) : periodFrom ? dayStartMsk(periodFrom) : undefined,
    created_to: singleDate ? dayEndMskExclusive(singleDate) : periodTo ? dayEndMskExclusive(periodTo) : undefined,
  }), [
    page, limit, debouncedSearch, moderationType, statusFilter,
    regionFilter, transportFilter, singleDate, periodFrom, periodTo,
  ])

  // При изменении любого фильтра возвращаемся на первую страницу
  useEffect(() => {
    setPage(1)
  }, [
    debouncedSearch,
    moderationType, statusFilter, regionFilter, transportFilter, singleDate, periodFrom, periodTo,
    setPage,
  ])

  const { data, isLoading, error } = usePendingCouriers(queryParams)

  // Детальная информация о курьере для модального окна
  const { data: courierDetails, isLoading: isLoadingDetails } = useUserDetails(
    selectedCourierId || 0,
    { enabled: isDetailModalOpen && !!selectedCourierId && selectedCourierId > 0 }
  )

  // История решений по выбранной заявке
  const { data: moderationHistory, isLoading: isHistoryLoading } = useModerationHistory(
    selectedCourierId || 0,
    isDetailModalOpen && !!selectedCourierId,
  )

  const handleApprove = (courier: ModerationItemResponse) => {
    // Тип курьера выбирается только для полной регистрации
    if (courier.moderation_type === 'full_registration') {
      setApproveTarget({
        userId: courier.user_id,
        fullName: courier.full_name,
        currentRole: courier.role,
      })
      return
    }

    void (async () => {
      if (!confirm(`Вы уверены, что хотите одобрить курьера "${courier.full_name}"?`)) {
        return
      }

      try {
        await approveCourier.mutateAsync({
          userId: courier.user_id,
          request: {
            action: 'approve',
            comment: 'Одобрен администратором'
          }
        })
      } catch (error) {
        // Ошибка уже обработана в хуке
      }
    })()
  }

  const handleApproveSubmit = async (role: number, comment: string) => {
    if (!approveTarget) return
    const { userId } = approveTarget

    try {
      await approveCourier.mutateAsync({
        userId,
        request: {
          action: 'approve',
          comment: comment || 'Одобрен администратором',
          role,
        }
      })
      setApproveTarget(null)
    } catch (error) {
      // Ошибка уже обработана в хуке
    }
  }

  const handleApproveClose = () => {
    setApproveTarget(null)
  }

  const handleReject = (userId: number, fullName: string) => {
    setRejectTarget({ userId, fullName })
  }

  const handleRejectSubmit = async (reason: string, fields: string[]) => {
    if (!rejectTarget) return
    const { userId } = rejectTarget

    try {
      await rejectCourier.mutateAsync({
        userId,
        request: {
          action: 'reject',
          comment: reason,
          reject_fields: fields,
        }
      })
      setRejectTarget(null)
    } catch (error) {
      // Ошибка уже обработана в хуке
    }
  }

  const handleRejectClose = () => {
    setRejectTarget(null)
  }

  const handleReopenSubmit = async () => {
    if (!reopenTarget) return
    try {
      await reopenCourier.mutateAsync({ userId: reopenTarget.userId, request: {} })
      setReopenTarget(null)
    } catch (error) {
      // Ошибка уже обработана в хуке
    }
  }

  const handleViewDetails = (courierId: number) => {
    setSelectedCourierId(courierId)
    setIsDetailModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsDetailModalOpen(false)
    setSelectedCourierId(null)
  }

  // Колонки таблицы: 6 компактных колонок без горизонтального скролла
  const columns: Column<ModerationItemResponse>[] = [
    {
      key: 'full_name',
      title: 'ФИО',
      width: '64',
      render: (_, courier) => (
        <div>
          <div className="font-medium text-gray-900">{courier.full_name}</div>
          {courier.email && (
            <div className="text-sm text-gray-500">{courier.email}</div>
          )}
        </div>
      ),
    },
    {
      key: 'status_text',
      title: 'Статус',
      width: '28',
      render: (_, courier) => {
        const s = courier.status_moderation
        const cls =
          s === 2
            ? 'bg-red-100 text-red-700'
            : s === 1
              ? 'bg-green-100 text-green-700'
              : 'bg-orange-100 text-orange-700'
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
            {courier.status_text || '—'}
          </span>
        )
      },
    },
    {
      key: 'phone',
      title: 'Телефон',
      width: '36',
      render: (phone) => phone || '—',
    },
    {
      key: 'created_at_formatted',
      title: 'Дата заявки',
      width: '32',
    },
    {
      key: 'region_name',
      title: 'Регион',
      width: '40',
      render: (region) => region || '—',
    },
    {
      key: 'transport_type_name',
      title: 'Вид транспорта',
      width: '40',
      render: (transport) => transport || '—',
    },
    {
      key: 'actions',
      title: 'Действия',
      width: '64',
      render: (_, courier) => {
        const isDriverLicenseUpdate = courier.moderation_type === 'driver_license_update'
        const statusModeration = courier.status_moderation ?? 0
        // DUL-очередь всегда actionable; для full_registration одобренные — только просмотр
        const showStandardActions = isDriverLicenseUpdate || statusModeration === 0
        const showReopen = !isDriverLicenseUpdate && statusModeration === 2

        return (
          <div className="flex space-x-2">
            {showStandardActions && (
              <>
                {hasPermission('moderation', 'approve') && (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleApprove(courier)
                    }}
                    isLoading={approveCourier.isPending}
                    disabled={approveCourier.isPending || rejectCourier.isPending}
                  >
                    Одобрить
                  </Button>
                )}
                {hasPermission('moderation', 'reject') && (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleReject(courier.user_id, courier.full_name)
                    }}
                    isLoading={rejectCourier.isPending}
                    disabled={approveCourier.isPending || rejectCourier.isPending}
                  >
                    Отклонить
                  </Button>
                )}
              </>
            )}
            {showReopen && hasPermission('moderation', 'approve') && (
              <Button
                size="sm"
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation()
                  setReopenTarget({ userId: courier.user_id, fullName: courier.full_name })
                }}
                disabled={reopenCourier.isPending}
              >
                Пересмотреть заявку
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  if (error) {
    return (
      <>
        <PageHeader 
          title="Модерация" 
          description="Сторонние курьеры ожидающие подтверждения" 
        />
        <div className="flex-1 p-6">
          <Card>
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-red-600 mb-2">
                Ошибка загрузки данных
              </h3>
              <p className="text-gray-500">{(error as any).detail || 'Произошла ошибка'}</p>
            </div>
          </Card>
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader 
        title="Модерация" 
        description="Сторонние курьеры ожидающие подтверждения"
      >
        {/* Статистика в заголовке */}
        {data && data.total > 0 && (
          <div className="flex items-center space-x-2">
            <div className="bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
              ⏳ {data.total} на модерации
            </div>
            {moderationType === 'full_registration' && (
              <div className="bg-gray-50 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                <UserPlusIcon className="w-4 h-4 inline mr-1" />
                Регистрация
              </div>
            )}
            {moderationType === 'driver_license_update' && (
              <div className="bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
                <DocumentTextIcon className="w-4 h-4 inline mr-1" />
                Обновление ВУ
              </div>
            )}
          </div>
        )}
      </PageHeader>
      
      <div className="px-6 pt-4">
        <SectionTabs
          items={[
            { label: 'Все курьеры', to: '/couriers' },
            { label: 'На модерации', to: '/moderation' },
          ]}
        />
      </div>
      
      <div className="flex-1 p-6 overflow-hidden flex flex-col min-h-0">
        {/* Поиск и фильтры */}
        <Card className="mb-6">
          <div className="space-y-4">
            {/* Текстовые фильтры и справочники */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Input
                placeholder="Поиск по имени, email, телефону..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <select
                className="admin-input"
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Все регионы</option>
                {(regions || []).map((region) => (
                  <option key={region.id} value={region.id}>{region.name}</option>
                ))}
              </select>

              <select
                className="admin-input"
                value={transportFilter}
                onChange={(e) => setTransportFilter(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Все виды транспорта</option>
                {(transportTypes?.items || []).map((transport) => (
                  <option key={transport.transport_type_id} value={transport.transport_type_id}>
                    {transport.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Даты: одиночная дата и период взаимно исключают друг друга */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500">Дата заявки</label>
                <DatePicker
                  value={singleDate}
                  onChange={(date) => {
                    setSingleDate(date)
                    // Выбор одиночной даты очищает период
                    if (date) {
                      setPeriodFrom(null)
                      setPeriodTo(null)
                    }
                  }}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500">Период с</label>
                <DatePicker
                  value={periodFrom}
                  onChange={(date) => {
                    setPeriodFrom(date)
                    // Выбор периода очищает одиночную дату
                    if (date) setSingleDate(null)
                  }}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500">Период по</label>
                <DatePicker
                  value={periodTo}
                  onChange={(date) => {
                    setPeriodTo(date)
                    // Выбор периода очищает одиночную дату
                    if (date) setSingleDate(null)
                  }}
                />
              </div>

              <div className="flex items-end">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSearch('')
                    setRegionFilter('')
                    setTransportFilter('')
                    setSingleDate(null)
                    setPeriodFrom(null)
                    setPeriodTo(null)
                    setModerationType('')
                    setStatusFilter('')
                    setPage(1)
                  }}
                >
                  Сбросить
                </Button>
              </div>
            </div>

            {/* Фильтры по типу модерации */}
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Тип заявки:
              </span>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant={moderationType === '' ? 'primary' : 'ghost'}
                  onClick={() => {
                    setModerationType('')
                    setPage(1)
                  }}
                  className="whitespace-nowrap"
                >
                  Все
                </Button>
                <Button
                  size="sm"
                  variant={moderationType === 'full_registration' ? 'primary' : 'ghost'}
                  onClick={() => {
                    setModerationType('full_registration')
                    setPage(1)
                  }}
                  className="whitespace-nowrap"
                >
                  <UserPlusIcon className="w-4 h-4 mr-1" />
                  Регистрация
                </Button>
                <Button
                  size="sm"
                  variant={moderationType === 'driver_license_update' ? 'primary' : 'ghost'}
                  onClick={() => {
                    setModerationType('driver_license_update')
                    setPage(1)
                  }}
                  className="whitespace-nowrap"
                >
                  <DocumentTextIcon className="w-4 h-4 mr-1" />
                  Обновление ВУ
                </Button>
              </div>
            </div>

            {/* Фильтры по статусу заявки */}
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Статус:
              </span>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant={statusFilter === 'on_moderation' ? 'primary' : 'ghost'}
                  onClick={() => {
                    setStatusFilter('on_moderation')
                    setPage(1)
                  }}
                  className="whitespace-nowrap"
                >
                  На модерации
                </Button>
                <Button
                  size="sm"
                  variant={statusFilter === 'rejected' ? 'primary' : 'ghost'}
                  onClick={() => {
                    setStatusFilter('rejected')
                    setPage(1)
                  }}
                  className="whitespace-nowrap"
                >
                  Отклонена
                </Button>
                <Button
                  size="sm"
                  variant={statusFilter === 'approved' ? 'primary' : 'ghost'}
                  onClick={() => {
                    setStatusFilter('approved')
                    setPage(1)
                  }}
                  className="whitespace-nowrap"
                >
                  Одобрена
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Таблица */}
        <DataTable
          data={data?.items || []}
          columns={columns}
          loading={isLoading}
          pagination={data ? {
            page: data.page,
            limit: data.limit,
            total: data.total,
            pages: data.pages,
            onPageChange: setPage,
          } : undefined}
          onRowClick={(courier) => {
            if (hasPermission('couriers', 'view_card')) {
              handleViewDetails(courier.user_id)
            }
          }}
          emptyMessage="Нет курьеров на модерации"
          fillHeight
          className={`flex-1 min-h-0 ${
            hasPermission('couriers', 'view_card') ? 'cursor-pointer' : ''
          }`}
        />

        {/* Модальное окно с детальной информацией */}
        <CourierDetailModal
          isOpen={isDetailModalOpen}
          onClose={handleCloseModal}
          courier={courierDetails ?? null}
          loading={isLoadingDetails}
          history={moderationHistory?.items}
          historyLoading={isHistoryLoading}
        />

        {/* Модальное окно одобрения с выбором типа курьера */}
        <ApproveModal
          isOpen={approveTarget !== null}
          onClose={handleApproveClose}
          courierName={approveTarget?.fullName ?? ''}
          currentRole={approveTarget?.currentRole}
          onSubmit={handleApproveSubmit}
          loading={approveCourier.isPending}
        />

        {/* Модальное окно отклонения */}
        <RejectModal
          isOpen={rejectTarget !== null}
          onClose={handleRejectClose}
          courierName={rejectTarget?.fullName ?? ''}
          onSubmit={handleRejectSubmit}
          loading={rejectCourier.isPending}
        />

        {/* Модальное окно пересмотра заявки */}
        <ReopenModal
          isOpen={reopenTarget !== null}
          onClose={() => setReopenTarget(null)}
          courierName={reopenTarget?.fullName ?? ''}
          onSubmit={handleReopenSubmit}
          loading={reopenCourier.isPending}
        />
      </div>
    </>
  )
}
